/**
 * AdditionalTableViewElement — kreator formatek: klocek palety reprezentujący
 * tabelę dodatkową (`additional_view`, patrz `RecursiveFormRenderer` PLACEHOLDER_TYPES).
 *
 * Mirror `AuditLogViewElement` / `DocumentItemsViewElement` — na REALNEJ stronie
 * rekordu ten typ jest dispatchowany WYŁĄCZNIE przez `contextRenderers['additional_view']`
 * (widget `AdditionalTableTab`, patrz `document-additional-renderers.tsx` i lokalne
 * mapy w `ContractorDetail.tsx`/`EmployeeDetail.tsx`/`UserDetail.tsx`). Ten plik
 * obsługuje TYLKO podgląd w canvasie buildera (`mode='builder'`).
 *
 * PO CO: tabela dodatkowa była dotąd dostępna wyłącznie jako CAŁA ZAKŁADKA (typ
 * zakładki `additional`, wybierany w `TabFormModal`). Ten klocek domyka rozjazd
 * względem „Pozycji dokumentu"/„Historii zmian" — tenant stawia tabelę dodatkową
 * TAM, GDZIE CHCE (w zakładce „Dane", w sekcji, w akordeonie), a dedykowana
 * zakładka zostaje jako alternatywa dla kogoś, kto woli osobną zakładkę.
 *
 * Wybór KTÓREJ tabeli dotyczy ten konkretny klocek dzieje się w `propertiesEditor`
 * (prawy panel buildera) — `AdditionalTableSlugPicker`, mirror `AdditionalTableConfigPanel`
 * z `SystemModuleBuilderPage.tsx` (ta sama funkcja filtrująca `filterAdditionalTablesForModule`,
 * ten sam katalog `useAdditionalTablesList`), tylko osadzony w edytorze WŁAŚCIWOŚCI ELEMENTU
 * (patrz `LayoutElementRegistry.propertiesEditor`) zamiast panelu konfiguracji całej zakładki.
 *
 * `allowedModules` — zakres tej iteracji to moduły, których strona detalu REALNIE
 * dispatchuje `additional_view` przez `contextRenderers` (zweryfikowane grepem):
 * 7 klas dokumentów (przez `buildDocumentAdditionalRenderers`) + `contractors` +
 * `employees` + `users`. Companies/org_units (F24 Pakiet 0, dawniej departments)/
 * contractor_contacts są na liście
 * rodziców w Ustawieniach → Tabele dodatkowe, ale ich strony detalu NIE mają
 * jeszcze wpięcia `contextRenderers['additional_view']` — dodać do listy razem
 * z wpięciem renderera na danej stronie, nie wcześniej (mirror ograniczenia
 * `AuditLogViewElement`, które z tego samego powodu ogranicza się do `products`).
 *
 * `render`/`propertiesEditor` są wołane jak ZWYKŁE FUNKCJE (nie komponenty) —
 * żaden hook nie może być wywołany BEZPOŚREDNIO w ich ciele; `propertiesEditor`
 * montuje `AdditionalTableSlugPicker`, a `render` — `AdditionalTableColumnsCanvas`
 * jako komponenty-dzieci (hooki żyją TAM).
 *
 * UKŁAD KOLUMN: gdy kreator ma dostęp do danych tenanta (`useBuilderEnvironment`),
 * płótno pokazuje edytor kolumn wybranej tabeli (`TableLayoutEditor`), który zapisuje
 * `settings.tableColumns` / `settings.hiddenColumnIds` tego umieszczenia tabeli;
 * bez dostępu (kreator osadzony w panelu ITE, hosty bez providera) — dotychczasowy
 * placeholder, a panel właściwości zamiast wyboru tabeli pokazuje krótką informację (wybór czyta
 * katalog tabel tenanta, więc nie jest montowany bez dostępu do jego danych). Na układzie
 * systemowym (`systemHideOnly`) edytor działa w trybie `hideOnly` — tenant tylko ukrywa kolumny
 * (`settings.hiddenColumnIds`), a kreator zapisuje to w nakładce tenanta pod kluczem tabeli
 * (`additional_table:<tableSlug>`). Realny render na stronie rekordu czyta te same klucze
 * (`AdditionalTableTab`).
 *
 * `label` jest literalnym stringiem, zgodnie z precedensem reszty palety
 * (paleta czyta `def.label` wprost, patrz `AuditLogViewElement` nagłówek).
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropdown } from '@/components/Dropdown';
import { useBuilderEnvironment } from '@/components/fields/builder-environment';
import type {
  LayoutElementDefinition,
  LayoutElementPropertiesEditorProps,
} from '@/components/fields/LayoutElementRegistry';
import { useAdditionalTablesList } from '@/lib/additional-tables/api';
import { filterAdditionalTablesForModule } from '@/pages/system-modules/document-module-slugs';

import {
  AdditionalTableColumnsCanvas,
  AdditionalTableViewPlaceholder,
} from './AdditionalTableColumnsCanvas';

/** Kartoteki, których strona detalu dispatchuje `additional_view` przez `contextRenderers`. */
const ADDITIONAL_TABLE_ALLOWED_MODULES = [
  'purchase_invoices',
  'sale_invoices',
  'contracts',
  'letters',
  'commercial',
  'internal',
  'specjalne',
  'contractors',
  'employees',
  'users',
];

/**
 * Bramka środowiska: wybór tabeli czyta katalog tabel TENANTA, więc w kreatorze bez dostępu do
 * danych tenanta (osadzonym w panelu ITE, hosty bez providera) pokazujemy tylko krótką
 * informację i NIE montujemy komponentu, który odpytuje API (jego hooki żyją w
 * `AdditionalTableSlugPickerLoaded`).
 */
function AdditionalTableSlugPicker(props: LayoutElementPropertiesEditorProps) {
  const { t } = useTranslation();
  const { tenantData } = useBuilderEnvironment();

  if (!tenantData) {
    return (
      <p
        className="text-sm text-text-tertiary"
        data-testid="additional-table-view-selector-unavailable"
      >
        {t('additionalTables.tableSelector.unavailableHere')}
      </p>
    );
  }
  return <AdditionalTableSlugPickerLoaded {...props} />;
}

function AdditionalTableSlugPickerLoaded({
  settings,
  onChange,
  moduleSlug,
}: LayoutElementPropertiesEditorProps) {
  const { t } = useTranslation();
  const tablesQuery = useAdditionalTablesList();
  const filtered = filterAdditionalTablesForModule(tablesQuery.data ?? [], moduleSlug ?? '');
  const value = typeof settings.tableSlug === 'string' ? settings.tableSlug : '';

  // Błąd pobrania katalogu (403 brak uprawnienia / 500 / sieć) NIE jest tym samym co
  // "brak tabel dla modułu" — pomieszanie tych dwóch stanów każe adminowi dodać coś,
  // co może już istnieć. Best-effort diagnostyka w konsoli (błąd katalogu NIE blokuje panelu).
  useEffect(() => {
    if (tablesQuery.isError) {
      console.warn(
        '[additional_view] Nie udało się pobrać katalogu tabel dodatkowych',
        tablesQuery.error,
      );
    }
  }, [tablesQuery.isError, tablesQuery.error]);

  return (
    <div className="space-y-2">
      <span className="block text-xs text-text-secondary">
        {t('additionalTables.tableSelector.hint')}
      </span>
      {tablesQuery.isPending ? (
        <p className="text-sm text-text-muted">{t('common.loading')}</p>
      ) : tablesQuery.isError ? (
        <p className="text-sm text-danger-600">{t('additionalTables.tableSelector.loadError')}</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-text-tertiary">
          {t('additionalTables.tableSelector.noTablesForModule')}
        </p>
      ) : (
        <Dropdown
          value={value}
          // Zmiana tabeli czyści układ kolumn poprzedniej tabeli (id kolumn są jej slugami pól) —
          // inaczej zostałby martwy zapis, a po powrocie do tamtej tabeli układ wróciłby niepytany.
          onChange={(v) =>
            onChange({ tableSlug: v, tableColumns: undefined, hiddenColumnIds: undefined })
          }
          data-testid="additional-table-view-selector"
          options={[
            { value: '', label: t('additionalTables.tableSelector.placeholder') },
            ...filtered.map((tbl) => ({ value: tbl.slug, label: tbl.displayName })),
          ]}
        />
      )}
    </div>
  );
}

export const AdditionalTableViewElement: LayoutElementDefinition = {
  type: 'additional_view',
  category: 'tresc',
  isContainer: false,
  label: 'Tabela dodatkowa',
  icon: 'Table2',
  allowedModules: ADDITIONAL_TABLE_ALLOWED_MODULES,
  // Edytor kolumn na płótnie jest szeroką tabelą — cały wiersz gridu sekcji, nie pół komórki.
  fullWidth: true,
  defaultSettings: () => ({}),
  propertiesEditor: (props) => <AdditionalTableSlugPicker {...props} />,
  // Płótno kreatora = edytor układu kolumn (albo placeholder bez dostępu do danych tenanta);
  // poza kreatorem zostaje sam placeholder — realną stronę rekordu obsługuje `contextRenderers`.
  // Komponenty-dzieci niosą hooki (ta funkcja jest wołana jak zwykła, bez hooków).
  render: ({ element, mode, onSettingsChange, systemHideOnly }) =>
    mode === 'builder' ? (
      <AdditionalTableColumnsCanvas
        element={element}
        onSettingsChange={onSettingsChange}
        systemHideOnly={systemHideOnly === true}
      />
    ) : (
      <AdditionalTableViewPlaceholder elementId={element.id} />
    ),
};
